import { useRef, useState } from 'react';
import { Mail, MessageSquare, Paperclip, Send, X } from 'lucide-react';
import { ChannelChip } from '@/components/ChannelChip';
import { VendorTrace } from '@/components/VendorTrace';
import { StatusBadge, type StatusTone } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn, channelLabel, formatDateTime } from '@/lib/utils';
import type { AdminOrderVendor, SupplierOrderStatusKey } from '@/lib/types';

export const SUPPLIER_STATUS_TONE: Record<SupplierOrderStatusKey, StatusTone> = {
  draft: 'ghost',
  queued: 'ghost',
  failed: 'red',
  awaiting: 'cyan',
  confirmed: 'green',
  delivered: 'green',
};

export function SupplierStatusBadge({ status }: { status: AdminOrderVendor['status'] }) {
  return (
    <StatusBadge tone={SUPPLIER_STATUS_TONE[status.key]} className="whitespace-nowrap">
      {status.label}
    </StatusBadge>
  );
}

const TRACE_TITLE: Record<string, string> = {
  sftp_edi: 'EDI exchange',
  website: 'Browser agent — supplier website',
  email: 'Email & SMS conversation',
  pdf_email: 'Email & SMS conversation',
};

// Right-hand panel for one supplier order: who/what/when, then the
// communication trace over the supplier's channel. Email/SMS suppliers get a
// composer so an admin can jump into the conversation.
export function SupplierOrderPanel({ vendor, className }: { vendor: AdminOrderVendor; className?: string }) {
  const po = vendor.purchaseOrder;
  const isMessaging = ['email', 'pdf_email'].includes(vendor.supplier.dispatchChannel);

  return (
    <div className={cn('card p-8', className)}>
      <div className="mb-6 flex items-start justify-between gap-3 border-b border-line pb-6">
        <div className="min-w-0 flex-1">
          <span className="eyebrow">{TRACE_TITLE[vendor.supplier.dispatchChannel] ?? 'Communication'}</span>
          <h2 className="section-head">{vendor.supplier.name}</h2>
          <dl className="mt-3 grid grid-cols-2 gap-x-6 gap-y-2 text-xs">
            <Field label="Our PO">
              <span className="font-mono text-brand">{po?.poNumber ?? '—'}</span>
            </Field>
            <Field label="Supplier order #">
              <span className="font-mono text-ink">{po?.supplierOrderRef ?? 'Pending'}</span>
            </Field>
            <Field label="Expected">
              <span className="text-ink">{po?.expectedDeliveryAt ? formatDateTime(po.expectedDeliveryAt) : '—'}</span>
            </Field>
          </dl>
          <p className="mt-2 text-xs text-muted">
            {vendor.supplier.emailAddress && <span>{vendor.supplier.emailAddress}</span>}
            {vendor.supplier.emailAddress && vendor.supplier.deliverySchedule && <span> · </span>}
            {vendor.supplier.deliverySchedule && <span>Delivers {vendor.supplier.deliverySchedule}</span>}
          </p>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-2">
          <ChannelChip channel={vendor.supplier.dispatchChannel} />
          <SupplierStatusBadge status={vendor.status} />
        </div>
      </div>

      {po?.failureReason && (
        <p className="mb-4 border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger">{po.failureReason}</p>
      )}

      {po ? (
        <VendorTrace channel={vendor.supplier.dispatchChannel} items={vendor.communications} />
      ) : (
        <div className="border border-dashed border-line px-4 py-10 text-center text-sm text-muted">
          Draft order — the {channelLabel(vendor.supplier.dispatchChannel)} PO to {vendor.supplier.name} will be created
          when the chef submits.
        </div>
      )}

      {po && isMessaging && <Composer key={po.id} supplierName={vendor.supplier.name} />}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-[10px] font-bold uppercase tracking-[0.12em] text-faint">{label}</dt>
      <dd className="truncate">{children}</dd>
    </div>
  );
}

// Admin reply box for email/SMS suppliers. UI only for the demo: nothing is
// sent; the ordering agent will own this conversation.
function Composer({ supplierName }: { supplierName: string }) {
  const [medium, setMedium] = useState<'email' | 'sms'>('email');
  const [text, setText] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [notice, setNotice] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  function send() {
    setNotice(true);
    setText('');
    setFiles([]);
  }

  return (
    <div className="mt-6 border-t border-line pt-4">
      <div className="mb-2 flex items-center justify-between gap-3">
        <span className="eyebrow">Jump into the conversation</span>
        <div className="flex border border-line text-xs" role="radiogroup" aria-label="Send as">
          {(['email', 'sms'] as const).map((m) => {
            const Icon = m === 'email' ? Mail : MessageSquare;
            return (
              <button
                key={m}
                type="button"
                role="radio"
                aria-checked={medium === m}
                onClick={() => setMedium(m)}
                className={cn(
                  'flex items-center gap-1.5 px-2.5 py-1 uppercase tracking-wide transition-colors',
                  medium === m ? 'bg-brand/15 text-brand' : 'text-muted hover:text-ink',
                )}
              >
                <Icon className="h-3 w-3" /> {m}
              </button>
            );
          })}
        </div>
      </div>
      <div className="border border-line bg-surface-2 focus-within:border-brand focus-within:ring-1 focus-within:ring-brand">
        <textarea
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            setNotice(false);
          }}
          rows={3}
          placeholder={`Write to ${supplierName} by ${medium === 'email' ? 'email' : 'SMS'}…`}
          className="block w-full resize-none bg-transparent px-3 py-2.5 text-sm text-ink placeholder:text-faint focus:outline-none"
        />
        {files.length > 0 && (
          <ul className="flex flex-wrap gap-1.5 px-3 pb-2">
            {files.map((f, i) => (
              <li
                key={`${f.name}-${i}`}
                className="inline-flex items-center gap-1.5 border border-line bg-surface px-2 py-0.5 text-xs text-muted"
              >
                <Paperclip className="h-3 w-3" />
                <span className="max-w-[12rem] truncate">{f.name}</span>
                <button
                  type="button"
                  onClick={() => setFiles((all) => all.filter((_, j) => j !== i))}
                  className="text-faint hover:text-danger"
                  aria-label={`Remove ${f.name}`}
                >
                  <X className="h-3 w-3" />
                </button>
              </li>
            ))}
          </ul>
        )}
        <div className="flex items-center justify-between gap-3 border-t border-line px-2 py-1.5">
          <button
            type="button"
            onClick={() => fileInput.current?.click()}
            className="flex items-center gap-1.5 rounded px-2 py-1 text-xs text-muted hover:bg-surface-3 hover:text-ink"
          >
            <Paperclip className="h-3.5 w-3.5" /> Attach file
          </button>
          <input
            ref={fileInput}
            type="file"
            multiple
            className="hidden"
            onChange={(e) => {
              const picked = Array.from(e.target.files ?? []);
              setFiles((all) => [...all, ...picked]);
              e.target.value = '';
            }}
          />
          <Button size="sm" onClick={send} disabled={!text.trim() && files.length === 0}>
            <Send className="h-3.5 w-3.5" /> Send
          </Button>
        </div>
      </div>
      {notice && (
        <p className="mt-2 text-xs text-amber">Demo only — admin messages aren&apos;t sent yet.</p>
      )}
    </div>
  );
}
